/**
 * Identity Alignment Assessment · Question flow.
 * Route: /identity/assessment
 *
 * One question per screen. Marketing groups the 40 questions into 5 themes,
 * but the backend has 9 sections — we show a section banner per backend section
 * and a progress bar across the whole flow.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { Label } from "@/components/ui/label";
import { identityApi } from "@/lib/identityApi";
import logo from "@/assets/brand/kissed-logo.png";
import { formatApiError } from "@/lib/formatError";

const KEY_ANSWERS = "kissed_identity_answers";
const KEY_INTRO = "kissed_identity_intro";

export default function IdentityAssessment() {
  useDocumentMeta({
    title: "Identity Alignment Assessment · Dr. Christy Vonta",
    description: "40-question Identity Alignment Assessment. Answer honestly to receive a personalized reflection and a recommended next step in your healing journey.",
  });
  const navigate = useNavigate();
  const [opts, setOpts] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0); // 0=intro, 1..N=question, N+1=submit
  const [answers, setAnswers] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(KEY_ANSWERS) || "{}"); } catch { return {}; }
  });
  const [intro, setIntro] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(KEY_INTRO) || "{}"); } catch { return {}; }
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    identityApi.options().then((r) => {
      setOpts(r.data);
      setQuestions(r.data?.questions || []);
    }).catch(() => toast.error("Could not load the assessment."));
  }, []);

  // Persist on change
  useEffect(() => { try { sessionStorage.setItem(KEY_ANSWERS, JSON.stringify(answers)); } catch { /* */ } }, [answers]);
  useEffect(() => { try { sessionStorage.setItem(KEY_INTRO, JSON.stringify(intro)); } catch { /* */ } }, [intro]);

  const total = questions.length;
  const currentQ = step >= 1 && step <= total ? questions[step - 1] : null;
  const progressPct = total === 0 ? 0 : Math.round((step / (total + 1)) * 100);

  const sectionFor = currentQ ? currentQ.section : "";
  const sectionIndex = useMemo(() => {
    if (!currentQ) return [0, 0];
    const sect = currentQ.section;
    const qsInSect = questions.filter((q) => q.section === sect);
    const localIdx = qsInSect.findIndex((q) => q.question_id === currentQ.question_id);
    return [localIdx + 1, qsInSect.length];
  }, [currentQ, questions]);

  const canIntroProceed = (intro.first_name || "").trim() && /\S+@\S+\.\S+/.test(intro.email || "");

  const recordAnswer = (qid, payload) => setAnswers((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...payload } }));

  const next = () => {
    if (step === 0) {
      if (!canIntroProceed) {
        toast.error("Please share your first name and email so we can send your report.");
        return;
      }
      setStep(1);
      window.scrollTo(0, 0);
      return;
    }
    if (currentQ) {
      const a = answers[currentQ.question_id] || {};
      if (currentQ.type === "multiple_choice" && !a.selected_letter) {
        toast.error("Please choose a response.");
        return;
      }
      if (currentQ.type === "short_answer" && !(a.text_response || "").trim()) {
        toast.error("Please share a brief reflection.");
        return;
      }
    }
    setStep((s) => s + 1);
    window.scrollTo(0, 0);
  };

  const back = () => {
    if (step > 0) setStep((s) => s - 1);
    window.scrollTo(0, 0);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        first_name: intro.first_name.trim(),
        email: intro.email.trim().toLowerCase(),
        phone: (intro.phone || "").trim() || null,
        answers: questions.map((q) => ({
          question_id: q.question_id,
          selected_letter: answers[q.question_id]?.selected_letter || null,
          text_response: answers[q.question_id]?.text_response || null,
        })),
      };
      const r = await identityApi.submit(payload);
      const d = r?.data;
      if (!d?.submission_id) throw new Error("No submission id returned");
      try {
        sessionStorage.setItem("kissed_identity_last_submission_id", d.submission_id);
        sessionStorage.setItem("kissed_identity_last_email", payload.email);
      } catch { /* */ }
      // Clear answer cache so a refresh of /assessment starts fresh.
      try { sessionStorage.removeItem(KEY_ANSWERS); sessionStorage.removeItem(KEY_INTRO); } catch { /* */ }
      navigate(`/identity/results?id=${encodeURIComponent(d.submission_id)}&email=${encodeURIComponent(payload.email)}`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not submit your assessment"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!opts) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading the assessment…
      </div>
    );
  }

  if (opts && opts.module_enabled === false) {
    return (
      <div className="min-h-screen bg-[#faf7f5] flex items-center justify-center">
        <div className="max-w-md mx-auto p-8 text-center">
          <img src={logo} alt="KISSED" className="h-12 mx-auto drop-shadow-md" />
          <h1 className="mt-4 font-display text-2xl text-slate-900">This experience is paused.</h1>
          <p className="mt-2 text-sm text-slate-700">
            The Identity Alignment Assessment is temporarily offline for maintenance. Please check back soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="identity-assessment" className="min-h-screen bg-[#faf7f5] text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/coaching/identity"><img src={logo} alt="KISSED" className="h-10 w-auto drop-shadow-md" /></Link>
          <span className="font-display text-sm text-slate-700">Identity Alignment Assessment</span>
        </div>
        <div className="max-w-3xl mx-auto px-6 pb-3">
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-900 transition-all"
              style={{ width: `${progressPct}%` }}
              data-testid="identity-progress-bar"
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
            <span>{step === 0 ? "Begin" : step > total ? "Final step" : `Question ${step} of ${total}`}</span>
            <span>{progressPct}%</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {step === 0 && (
          <div data-testid="identity-intro">
            <div className="text-[11px] tracking-[0.18em] font-semibold text-purple-900 uppercase">Before we begin</div>
            <h1 className="mt-2 font-display text-3xl text-slate-900">Let's send your report to the right person.</h1>
            <p className="mt-3 text-sm text-slate-700">
              We'll email your personalized PDF and keep your reflection safe. Your information is held with care.
            </p>
            <div className="mt-8 grid gap-5 max-w-md">
              <div>
                <Label htmlFor="first_name">First name</Label>
                <Input
                  id="first_name"
                  value={intro.first_name || ""}
                  onChange={(e) => setIntro({ ...intro, first_name: e.target.value })}
                  placeholder="What shall we call you?"
                  data-testid="identity-intro-first-name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={intro.email || ""}
                  onChange={(e) => setIntro({ ...intro, email: e.target.value })}
                  placeholder="you@example.com"
                  data-testid="identity-intro-email"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input
                  id="phone"
                  value={intro.phone || ""}
                  onChange={(e) => setIntro({ ...intro, phone: e.target.value })}
                  placeholder="(555) 555-0000"
                  data-testid="identity-intro-phone"
                />
              </div>
            </div>
          </div>
        )}

        {step >= 1 && step <= total && currentQ && (
          <div data-testid={`identity-question-${currentQ.question_id}`}>
            <div className="text-[11px] tracking-[0.18em] font-semibold text-purple-900 uppercase">
              {sectionFor} · {sectionIndex[0]} of {sectionIndex[1]}
            </div>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl text-slate-900 leading-snug">
              {currentQ.prompt}
            </h2>

            {currentQ.type === "multiple_choice" ? (
              <div className="mt-8 space-y-3" role="radiogroup">
                {currentQ.options.map((opt) => {
                  const chosen = answers[currentQ.question_id]?.selected_letter === opt.letter;
                  return (
                    <button
                      key={opt.letter}
                      role="radio"
                      aria-checked={chosen}
                      onClick={() => recordAnswer(currentQ.question_id, { selected_letter: opt.letter, text_response: null })}
                      data-testid={`identity-option-${currentQ.question_id}-${opt.letter}`}
                      className={`w-full text-left rounded-xl border px-5 py-4 flex items-start gap-3 transition ${
                        chosen
                          ? "border-purple-900 bg-purple-50 ring-2 ring-purple-300"
                          : "border-slate-200 bg-white hover:border-purple-300"
                      }`}
                    >
                      <div className={`flex-none w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center ${
                        chosen ? "bg-purple-900 text-white" : "bg-slate-100 text-slate-700"
                      }`}>
                        {chosen ? <Check className="w-4 h-4" /> : opt.letter}
                      </div>
                      <div className="text-sm leading-relaxed text-slate-900">{opt.text}</div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="mt-8">
                <textarea
                  data-testid={`identity-text-${currentQ.question_id}`}
                  rows={5}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 bg-white"
                  placeholder="A short reflection — there is no right answer here."
                  value={answers[currentQ.question_id]?.text_response || ""}
                  onChange={(e) => recordAnswer(currentQ.question_id, { text_response: e.target.value, selected_letter: null })}
                />
              </div>
            )}
          </div>
        )}

        {step === total + 1 && (
          <div data-testid="identity-confirm-submit">
            <div className="text-[11px] tracking-[0.18em] font-semibold text-purple-900 uppercase">One final step</div>
            <h2 className="mt-2 font-display text-3xl text-slate-900">You've shared what you needed to share.</h2>
            <p className="mt-3 text-sm text-slate-700">
              When you're ready, submit your reflection. We'll generate your personalized report and email it to
              <span className="font-semibold"> {intro.email}</span>.
            </p>
            <div className="mt-8">
              <Button
                onClick={submit}
                disabled={submitting}
                size="lg"
                className="bg-purple-900 hover:bg-purple-800 text-white rounded-full px-8"
                data-testid="identity-submit-btn"
              >
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>) : (<>See My Reflection <ArrowRight className="w-4 h-4 ml-1" /></>)}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-12 flex items-center justify-between">
          <Button variant="ghost" onClick={back} disabled={step === 0} data-testid="identity-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {step <= total && (
            <Button
              onClick={next}
              className="bg-purple-900 hover:bg-purple-800 text-white rounded-full px-6"
              data-testid="identity-next-btn"
            >
              {step === total ? "Continue" : "Next"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
